import React from 'react';

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { SvgIconComponent } from '@mui/icons-material';

import BieOnboardingStatusChip, {
  BieOnboardingStatusChipKind,
} from './BieOnboardingStatusChip';

/* ============================================================
 * Types
 * ============================================================
 */

export type BieOnboardingSummaryCardTone =
  | 'DEFAULT'
  | 'PRIMARY'
  | 'INFO'
  | 'SUCCESS'
  | 'WARNING'
  | 'ERROR';

export interface IBieOnboardingSummaryCardProps {
  /**
   * Main card heading.
   *
   * Examples:
   *
   * Imported Records
   * Valid Records
   * Blocking Issues
   */
  title: string;

  /**
   * Main numeric or textual value.
   */
  value: string | number | null | undefined;

  /**
   * Optional supporting description.
   */
  subtitle?: string;

  /**
   * Optional secondary value displayed beneath the main value.
   */
  secondaryValue?: string | number;

  /**
   * Optional secondary-value caption.
   */
  secondaryLabel?: string;

  /**
   * Optional MUI icon.
   */
  icon?: SvgIconComponent;

  /**
   * Card visual tone.
   */
  tone?: BieOnboardingSummaryCardTone;

  /**
   * Optional status chip.
   */
  statusKind?: BieOnboardingStatusChipKind;

  statusValue?: string | number | boolean | null;

  statusLabel?: string;

  statusBlocking?: boolean | number | null;

  /**
   * Displays a loading skeleton when true.
   */
  loading?: boolean;

  /**
   * Displays a small operation spinner without replacing the
   * existing value.
   */
  busy?: boolean;

  /**
   * Optional interaction.
   */
  clickable?: boolean;

  selected?: boolean;

  disabled?: boolean;

  onClick?: () => void;

  tooltip?: string;

  compact?: boolean;

  sx?: SxProps<Theme>;
}

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Reusable KPI and summary card for the BIE Institutional
 * Onboarding Workbench.
 *
 * Typical uses:
 *
 * - Total imported records
 * - Valid records
 * - Warning records
 * - Failed records
 * - Blocking issues
 * - Unresolved references
 */
export default class BieOnboardingSummaryCard extends React.PureComponent<IBieOnboardingSummaryCardProps> {
  public render(): React.ReactNode {
    const {
      clickable = false,
      disabled = false,
      onClick,
      tooltip,
    } = this.props;

    const card = (
      <Card variant="outlined" sx={this.getCardStyle()}>
        {clickable ? (
          <CardActionArea
            disabled={disabled}
            onClick={onClick}
            sx={{
              height: '100%',
              alignItems: 'stretch',
            }}
          >
            {this.renderContent()}
          </CardActionArea>
        ) : (
          this.renderContent()
        )}
      </Card>
    );

    if (!tooltip) {
      return card;
    }

    return (
      <Tooltip title={tooltip} arrow>
        <Box
          component="span"
          sx={{
            display: 'block',
            height: '100%',
          }}
        >
          {card}
        </Box>
      </Tooltip>
    );
  }

  /* ==========================================================
   * Content
   * ==========================================================
   */

  private renderContent(): React.ReactNode {
    const { loading = false, compact = false } = this.props;

    return (
      <CardContent
        sx={{
          height: '100%',

          p: compact ? 1.75 : 2.25,

          '&:last-child': {
            pb: compact ? 1.75 : 2.25,
          },
        }}
      >
        {loading ? this.renderLoading() : this.renderSummary()}
      </CardContent>
    );
  }

  private renderSummary(): React.ReactNode {
    const {
      title,
      subtitle,
      secondaryValue,
      secondaryLabel,
      icon: Icon,
      statusKind,
      statusValue,
      statusLabel,
      statusBlocking,
      busy = false,
      compact = false,
    } = this.props;

    return (
      <Stack
        spacing={compact ? 1 : 1.5}
        sx={{
          height: '100%',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant={compact ? 'caption' : 'body2'}
              color="text.secondary"
              fontWeight={600}
              sx={{
                letterSpacing: 0.2,
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.25,

                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {(Icon || busy) && (
            <Box sx={this.getIconContainerStyle()}>
              {busy ? (
                <CircularProgress size={compact ? 18 : 22} thickness={4} />
              ) : Icon ? (
                <Icon fontSize={compact ? 'small' : 'medium'} />
              ) : null}
            </Box>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          spacing={1.5}
          sx={{
            mt: 'auto',
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant={compact ? 'h5' : 'h4'}
              component="div"
              fontWeight={700}
              sx={{
                lineHeight: 1.1,
                overflowWrap: 'anywhere',
              }}
            >
              {this.formatValue(this.props.value)}
            </Typography>

            {secondaryValue !== undefined && secondaryValue !== null && (
              <Stack
                direction="row"
                alignItems="baseline"
                spacing={0.5}
                sx={{
                  mt: 0.75,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {this.formatValue(secondaryValue)}
                </Typography>

                {secondaryLabel && (
                  <Typography variant="caption" color="text.secondary">
                    {secondaryLabel}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {statusKind && (
            <Box
              sx={{
                flexShrink: 0,
              }}
            >
              <BieOnboardingStatusChip
                kind={statusKind}
                value={statusValue}
                label={statusLabel}
                blocking={statusBlocking}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  }

  private renderLoading(): React.ReactNode {
    const { compact = false } = this.props;

    return (
      <Stack spacing={compact ? 1 : 1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Skeleton variant="text" width="48%" height={24} />

          <Skeleton
            variant="circular"
            width={compact ? 28 : 36}
            height={compact ? 28 : 36}
          />
        </Stack>

        <Skeleton variant="text" width="40%" height={compact ? 38 : 48} />

        <Skeleton variant="text" width="65%" height={20} />
      </Stack>
    );
  }

  /* ==========================================================
   * Styling
   * ==========================================================
   */

  private getCardStyle(): SxProps<Theme> {
    const {
      selected = false,
      disabled = false,
      clickable = false,
      tone = 'DEFAULT',
      sx,
    } = this.props;

    return {
      height: '100%',
      minHeight: this.props.compact ? 120 : 150,

      position: 'relative',
      overflow: 'hidden',

      borderColor: (theme: Theme) => {
        if (selected) {
          return theme.palette.primary.main;
        }

        return this.getToneColor(theme, tone);
      },

      borderWidth: selected ? 2 : 1,

      opacity: disabled ? 0.6 : 1,

      transition:
        'border-color 160ms ease, ' +
        'box-shadow 160ms ease, ' +
        'transform 160ms ease',

      '&::before': {
        content: '""',

        position: 'absolute',
        top: 0,
        left: 0,

        width: 4,
        height: '100%',

        backgroundColor: (theme: Theme) => this.getToneColor(theme, tone),
      },

      ...(clickable && !disabled
        ? {
            '&:hover': {
              transform: 'translateY(-1px)',

              boxShadow: (theme: Theme) => theme.shadows[3],
            },
          }
        : {}),

      ...sx,
    };
  }

  private getIconContainerStyle(): SxProps<Theme> {
    const { tone = 'DEFAULT', compact = false } = this.props;

    return {
      width: compact ? 34 : 42,

      height: compact ? 34 : 42,

      borderRadius: 2,

      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      flexShrink: 0,

      color: (theme: Theme) => this.getToneColor(theme, tone),

      backgroundColor: (theme: Theme) =>
        this.getToneBackgroundColor(theme, tone),
    };
  }

  private getToneColor(
    theme: Theme,
    tone: BieOnboardingSummaryCardTone
  ): string {
    switch (tone) {
      case 'PRIMARY':
        return theme.palette.primary.main;

      case 'INFO':
        return theme.palette.info.main;

      case 'SUCCESS':
        return theme.palette.success.main;

      case 'WARNING':
        return theme.palette.warning.main;

      case 'ERROR':
        return theme.palette.error.main;

      case 'DEFAULT':
      default:
        return theme.palette.divider;
    }
  }

  private getToneBackgroundColor(
    theme: Theme,
    tone: BieOnboardingSummaryCardTone
  ): string {
    switch (tone) {
      case 'PRIMARY':
        return theme.palette.primary.main + '14';

      case 'INFO':
        return theme.palette.info.main + '14';

      case 'SUCCESS':
        return theme.palette.success.main + '14';

      case 'WARNING':
        return theme.palette.warning.main + '14';

      case 'ERROR':
        return theme.palette.error.main + '14';

      case 'DEFAULT':
      default:
        return theme.palette.action.hover;
    }
  }

  /* ==========================================================
   * Helpers
   * ==========================================================
   */

  private formatValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return new Intl.NumberFormat().format(value);
    }

    const normalized = String(value).trim();

    if (/^-?\d+(?:\.\d+)?$/.test(normalized)) {
      const numericValue = Number(normalized);

      if (
        Number.isFinite(numericValue) &&
        Math.abs(numericValue) <= Number.MAX_SAFE_INTEGER
      ) {
        return new Intl.NumberFormat().format(numericValue);
      }
    }

    return normalized || '—';
  }
}
